import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPrivate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, CheckCircle2, User, FileText, Bell, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
    { id: "welcome", icon: Sparkles, title: "Benvenuto" },
    { id: "profile", icon: User, title: "Profilo" },
    { id: "docs", icon: FileText, title: "Documenti" },
    { id: "notifications", icon: Bell, title: "Notifiche" },
    { id: "ready", icon: CheckCircle2, title: "Tutto pronto" },
];

export default function Onboarding() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(0);
    const [profile, setProfile] = useState({ pec: "", telefono: "", indirizzo: "" });
    const [state, setState] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        apiPrivate.get("/onboarding/state").then(r => {
            setState(r.data);
            if (r.data.onboarding_completed) navigate("/app", { replace: true });
        }).catch(() => {});
        apiPrivate.get("/soci/me").then(r => {
            setProfile({
                pec: r.data.pec || "",
                telefono: r.data.telefono || "",
                indirizzo: r.data.indirizzo || "",
            });
        }).catch(() => {});
    }, [navigate]);

    const saveProfile = async () => {
        setSaving(true);
        try {
            await apiPrivate.patch("/soci/me", profile);
            const r = await apiPrivate.get("/onboarding/state");
            setState(r.data);
            toast.success("Dati di contatto salvati");
            setStep(step + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
        setSaving(false);
    };

    const finish = async () => {
        setSaving(true);
        await apiPrivate.post("/onboarding/complete");
        navigate("/app", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white grain" data-testid="onboarding-page">
            <div className="max-w-2xl mx-auto pt-16 pb-24 px-6">
                {/* Progress */}
                <div className="flex items-center gap-2 mb-12" data-testid="onboarding-progress">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        const active = i === step;
                        const done = i < step;
                        return (
                            <div key={s.id} className="flex-1 flex items-center gap-2">
                                <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition ${
                                    done ? "bg-emerald-500/20 text-emerald-300" :
                                    active ? "bg-[linear-gradient(135deg,#ed4735,#df46e7,#653fcd)] text-white" :
                                    "bg-white/5 text-zinc-600 border border-white/10"
                                }`}>
                                    <Icon className="h-4 w-4" />
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div className={`flex-1 h-px ${done ? "bg-emerald-400/40" : "bg-white/10"}`} />
                                )}
                            </div>
                        );
                    })}
                </div>

                {step === 0 && (
                    <div className="fade-up" data-testid="step-welcome">
                        <span className="label-tag">— Step 1 di 5</span>
                        <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">
                            Ciao <span className="font-serif-italic text-gradient-tropical">{user?.nome || "socio"}</span>.
                        </h1>
                        <p className="text-zinc-400 mt-6 leading-relaxed">
                            Benvenuto sulla piattaforma <strong className="text-white">StartupItalia Investor</strong>. In pochi passi configuriamo il tuo profilo e ti mostriamo come orientarti tra documenti, notifiche e FAQ.
                        </p>
                        <button onClick={() => setStep(1)} className="btn-rebel mt-8" data-testid="welcome-next">Iniziamo <ArrowRight className="h-4 w-4" /></button>
                    </div>
                )}

                {step === 1 && (
                    <div className="fade-up" data-testid="step-profile">
                        <span className="label-tag">— Step 2 di 5</span>
                        <h1 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">Completa i tuoi <span className="font-serif-italic text-gradient-tropical">contatti</span>.</h1>
                        <p className="text-zinc-400 mt-4">Servono per inviarti notifiche su documenti, assemblee e comunicazioni ufficiali.</p>
                        <div className="space-y-4 mt-8">
                            <Field label="PEC email" v={profile.pec} on={(v) => setProfile(p => ({ ...p, pec: v }))} testid="onb-pec" type="email" />
                            <Field label="Telefono" v={profile.telefono} on={(v) => setProfile(p => ({ ...p, telefono: v }))} testid="onb-tel" />
                            <Field label="Residenza / Sede legale" v={profile.indirizzo} on={(v) => setProfile(p => ({ ...p, indirizzo: v }))} testid="onb-indirizzo" />
                        </div>
                        {state && (
                            <div className="mt-6 text-xs text-zinc-500 font-grotesk">
                                Profilo completo al <strong className="text-bayside">{state.completion_percent}%</strong>
                            </div>
                        )}
                        <div className="flex gap-3 mt-8">
                            <button onClick={() => setStep(2)} className="btn-ghost" data-testid="profile-skip">Salta per ora</button>
                            <button onClick={saveProfile} disabled={saving} className="btn-rebel" data-testid="profile-save">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Salva e continua <ArrowRight className="h-4 w-4" /></>}
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="fade-up" data-testid="step-docs">
                        <span className="label-tag">— Step 3 di 5</span>
                        <h1 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">I tuoi <span className="font-serif-italic text-gradient-tropical">documenti</span>.</h1>
                        <p className="text-zinc-400 mt-4 leading-relaxed">
                            Nella sezione <strong className="text-white">Documenti</strong> trovi tutti i file ufficiali a te destinati:
                        </p>
                        <ul className="mt-6 space-y-3">
                            <Bullet>Certificati di partecipazione</Bullet>
                            <Bullet>Verbali di assemblea e CdA</Bullet>
                            <Bullet>Comunicazioni ufficiali societarie</Bullet>
                            <Bullet>Tutto suddiviso per <em>quota A/B/C/D</em> e per <em>tuoi documenti individuali</em></Bullet>
                        </ul>
                        <div className="flex gap-3 mt-8">
                            <button onClick={() => setStep(1)} className="btn-ghost">Indietro</button>
                            <button onClick={() => setStep(3)} className="btn-rebel" data-testid="docs-next">Continua <ArrowRight className="h-4 w-4" /></button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="fade-up" data-testid="step-notifications">
                        <span className="label-tag">— Step 4 di 5</span>
                        <h1 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">Notifiche e <span className="font-serif-italic text-gradient-tropical">assistenza</span>.</h1>
                        <p className="text-zinc-400 mt-4 leading-relaxed">
                            Riceverai un'email a <strong className="text-white">{user?.email}</strong> a ogni nuovo documento o comunicazione importante. Inoltre, in alto a destra trovi sempre l'<strong className="text-white">assistente virtuale</strong>: cerca tra le FAQ o inoltra una domanda al team Investor Relations.
                        </p>
                        <div className="bento p-5 mt-6 flex items-start gap-3" data-testid="onb-privacy">
                            <ShieldCheck className="h-5 w-5 text-bayside shrink-0 mt-0.5" />
                            <div>
                                <div className="font-display text-base">I tuoi dati sono al sicuro</div>
                                <p className="text-xs text-zinc-400 mt-1">Server in territorio UE, conformità GDPR, accesso passwordless via OTP, backup giornalieri.</p>
                            </div>
                        </div>
                        <div className="flex gap-3 mt-8">
                            <button onClick={() => setStep(2)} className="btn-ghost">Indietro</button>
                            <button onClick={() => setStep(4)} className="btn-rebel" data-testid="notif-next">Continua <ArrowRight className="h-4 w-4" /></button>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="fade-up text-center" data-testid="step-ready">
                        <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
                        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter">Tutto <span className="font-serif-italic text-gradient-tropical">pronto</span>.</h1>
                        <p className="text-zinc-400 mt-6 max-w-md mx-auto">
                            Hai completato l'onboarding. La tua dashboard ti aspetta.
                        </p>
                        <button onClick={finish} disabled={saving} className="btn-rebel mt-10 mx-auto" data-testid="finish-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Vai alla dashboard <ArrowRight className="h-4 w-4" /></>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, v, on, testid, type = "text" }) {
    return (
        <div>
            <div className="label-tag mb-2">{label}</div>
            <input
                type={type}
                value={v}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-3 font-grotesk focus:outline-none focus:border-bayside rounded-xl"
            />
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-3 text-zinc-300">
            <ArrowRight className="h-4 w-4 mt-1 text-bayside shrink-0" />
            <span>{children}</span>
        </li>
    );
}
